// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import * as api from '../../api/client'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useAutoRefresh, VIEW_REFRESH_MS } from '../../components/useAutoRefresh'
import { RefreshControl } from '../../components/RefreshControl'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState, EmptyState, relativeTime } from '../../components/workflow/StatePills'
import { useColumnWidths, ColumnResizeHandle } from '../../components/common/ColumnResize'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// D-VIEWS-COLUMN-RESIZE (86cb27wub): this tab's headers were hardcoded <th> literals, so the
// labels are lifted into arrays — they are the column KEYS the widths persist against, and they
// keep header and <colgroup> in lockstep (a mismatch would size the wrong column).
const PENDING_COLS = ['Patient', 'Drug', 'Requested by', 'When'] as const
const DECIDED_COLS = ['Patient', 'Drug', 'Decision', 'By', 'Reason', 'When', 'Resulting Rx'] as const

// D-UI-2: a funnel on every column. Keys are the header labels above, so header and funnel stay
// in lockstep. Each value is what the cell shows; dates stay raw so the funnel compares by day.
const PENDING_FUNNELS: FunnelColumn<api.RefillRequest>[] = [
  { key: 'Patient', label: 'Patient', value: r => r.patient_name },
  { key: 'Drug', label: 'Drug', value: r => r.drug_name },
  { key: 'Requested by', label: 'Requested by', value: r => r.requested_by_account },
  { key: 'When', label: 'When', value: r => r.requested_at },
]
const DECIDED_FUNNELS: FunnelColumn<api.RefillRequest>[] = [
  { key: 'Patient', label: 'Patient', value: r => r.patient_name },
  { key: 'Drug', label: 'Drug', value: r => r.drug_name },
  { key: 'Decision', label: 'Decision', value: r => r.status },
  { key: 'By', label: 'By', value: r => r.decided_by_account },
  { key: 'Reason', label: 'Reason', value: r => r.decision_reason, kind: 'text' },
  { key: 'When', label: 'When', value: r => r.decided_at },
  { key: 'Resulting Rx', label: 'Resulting Rx', value: r => (r.result_rx_id ? 'Open' : null) },
]

/**
 * Refill requests view (read-only per ADR-014 Phase B).
 *
 * Patient-initiated refill requests appear here. Pharmacy staff fulfill or
 * deny refills in PioneerRx; the resulting state is mirrored back via the
 * PioneerRx ingest. The portal does not surface action buttons.
 */
export default function RefillsTab() {
  const navigate = useNavigate()

  const [items, setItems] = useState<api.RefillRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // D-VIEWS-COLUMN-RESIZE (86cb27wub): the two tables on this tab size independently — they share
  // column labels ("Patient", "Drug", "When") but are different lists, so one key each.
  const pendResize = useColumnWidths('rf:pending', PENDING_COLS as unknown as string[])
  const decResize = useColumnWidths('rf:decided', DECIDED_COLS as unknown as string[])

  async function reload(silent = false) {
    if (!silent) setLoading(true)
    setError(null)
    try {
      const data = await api.listRefills()
      setItems(data.items)
    } catch (e: any) {
      setError(friendlyError(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { reload() }, [])

  // D-VIEWS: 15s auto-refresh, visibility-gated, silent (list swaps in place)
  const ar = useAutoRefresh(() => reload(true), { intervalMs: VIEW_REFRESH_MS })

  const pending = useMemo(() => items.filter(r => r.status === 'pending'), [items])
  const decided = useMemo(() => items.filter(r => r.status !== 'pending'), [items])

  // D-UI-2: the funnels filter the WHOLE list; paging happens after. A changed filter returns to
  // page 1 (fx.sig); an auto-refresh does not change the sig, so the page still survives it.
  const pendFx = useTableFunnels(pending, PENDING_FUNNELS)
  const decFx = useTableFunnels(decided, DECIDED_FUNNELS)

  const pend = usePaged(pendFx.rows, DEFAULT_PAGE_SIZE, pendFx.sig)  // D-VIEWS: no count-based resetKey → page survives auto-refresh
  // Recent decisions grows without bound (every fulfilled/denied refill ever) — page it too,
  // same engine, so the DOM never holds the full history.
  const dec = usePaged(decFx.rows, DEFAULT_PAGE_SIZE, decFx.sig)  // D-VIEWS: page survives auto-refresh

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
        <h2 style={h2Style}>Refill requests</h2>
        <p style={subtitleStyle}>
          Patient-initiated refill requests. Pharmacy staff fulfill or deny in
          PioneerRx; the result mirrors back here automatically.
        </p>
        </div>
        <RefreshControl ar={ar} />
      </div>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      <h3 style={sectionH3}>Pending ({pending.length})</h3>
      <div style={cardStyle}>
        {loading ? <LoadingState label="Loading refill requests…" /> :
         pending.length === 0 ? (
          <EmptyState message="No pending refill requests." />
        ) : (
          <>
          <Pager page={pend.page} totalPages={pend.totalPages} total={pend.total} pageSize={pend.pageSize} onPage={pend.setPage} onPageSize={pend.setPageSize} label="refills" />
          {pendFx.chips}
          <table {...pendFx.tableProps} ref={pendResize.tableRef} className={pendResize.tableClass} style={{ ...tableStyle, tableLayout: pendResize.tableLayout }}>
            <colgroup>
              {PENDING_COLS.map(c => <col key={c} style={{ width: pendResize.widthFor(c) }} />)}
              <col />{/* actions column — not resizable */}
            </colgroup>
            <thead>
              <tr>
                {PENDING_COLS.map((c, ci) => (
                  <th key={c} style={thStyle}>
                    {c} {pendFx.funnel(c)}
                    <ColumnResizeHandle colKey={c} colIndex={ci} api={pendResize} label={c} />
                  </th>
                ))}
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {pend.pageRows.map(req => (
                <tr key={req.id} style={trStyle}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 500 }}>{req.patient_name}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 500 }}>{req.drug_name}</div>
                    <div style={mutedStyle}>{req.drug_strength}</div>
                  </td>
                  <td style={tdStyle}>{req.requested_by_account}</td>
                  <td style={tdStyle}>{relativeTime(req.requested_at)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button
                      style={openLinkBtn}
                      onClick={() => navigate(`/rx/${req.rx_item_id}`)}
                      title="View original prescription"
                    >
                      <ExternalLink size={13} aria-hidden="true" /> Original
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={pend.page} totalPages={pend.totalPages} total={pend.total} pageSize={pend.pageSize} onPage={pend.setPage} showPageSize={false} label="refills" />
          </>
        )}
      </div>

      <h3 style={sectionH3}>Recent decisions ({decided.length})</h3>
      <div style={cardStyle}>
        {decided.length === 0 ? (
          <EmptyState message="No refill decisions yet." />
        ) : (
          <>
          <Pager page={dec.page} totalPages={dec.totalPages} total={dec.total} pageSize={dec.pageSize} onPage={dec.setPage} onPageSize={dec.setPageSize} label="decisions" />
          {decFx.chips}
          <table {...decFx.tableProps} ref={decResize.tableRef} className={decResize.tableClass} style={{ ...tableStyle, tableLayout: decResize.tableLayout }}>
            <colgroup>
              {DECIDED_COLS.map(c => <col key={c} style={{ width: decResize.widthFor(c) }} />)}
            </colgroup>
            <thead>
              <tr>
                {DECIDED_COLS.map((c, ci) => (
                  <th key={c} style={thStyle}>
                    {c} {decFx.funnel(c)}
                    <ColumnResizeHandle colKey={c} colIndex={ci} api={decResize} label={c} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dec.pageRows.map(req => (
                <tr key={req.id} style={trStyle}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 500 }}>{req.patient_name}</div>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 500 }}>{req.drug_name}</div>
                    <div style={mutedStyle}>{req.drug_strength}</div>
                  </td>
                  <td style={tdStyle}>
                    <span style={req.status === 'fulfilled' ? statusOk : statusDeny}>{req.status}</span>
                  </td>
                  <td style={tdStyle}>{req.decided_by_account ?? '—'}</td>
                  <td style={tdStyle}>{req.decision_reason ?? '—'}</td>
                  <td style={tdStyle}>{req.decided_at ? relativeTime(req.decided_at) : '—'}</td>
                  <td style={tdStyle}>
                    {req.result_rx_id ? (
                      <button style={openLinkBtn} onClick={() => navigate(`/rx/${req.result_rx_id}`)}>
                        <ExternalLink size={12} aria-hidden="true" /> Open
                      </button>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={dec.page} totalPages={dec.totalPages} total={dec.total} pageSize={dec.pageSize} onPage={dec.setPage} showPageSize={false} label="decisions" />
          </>
        )}
      </div>
    </div>
  )
}

const h2Style: React.CSSProperties = { fontSize: 18, fontWeight: 600, margin: 0 }
const subtitleStyle: React.CSSProperties = { margin: '4px 0 0', color: 'var(--color-text-soft)', fontSize: 13 }
const sectionH3: React.CSSProperties = { fontSize: 13, fontWeight: 600, margin: '20px 0 8px', color: 'var(--color-text-soft)', textTransform: 'uppercase', letterSpacing: 0.5 }
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8, overflow: 'hidden', marginBottom: 16,
}
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px',
  background: 'var(--color-surface-alt)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 11, fontWeight: 600,
  letterSpacing: 0.4, textTransform: 'uppercase',
  color: 'var(--color-text-mute)',
}
const trStyle: React.CSSProperties = { borderBottom: '1px solid var(--color-border)' }
const tdStyle: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'middle' }
const mutedStyle: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-mute)', marginTop: 2 }

const buttonBase: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 10px', fontSize: 12, fontWeight: 500,
  borderRadius: 4, border: '1px solid var(--color-border)',
  cursor: 'pointer', fontFamily: 'inherit',
}
const openLinkBtn: React.CSSProperties = { ...buttonBase, background: 'transparent', color: 'var(--color-text-soft)' }
const statusOk:   React.CSSProperties = { color: '#15803d', fontWeight: 500 }
const statusDeny: React.CSSProperties = { color: '#dc2626', fontWeight: 500 }
